// prop-type is a library for typechecking of props
/* eslint-disable */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { ErrorMessage } from "formik";
import { Grid } from "@mui/material";
import GoogleMapReact from "google-map-react";
import isEqual from "lodash/isEqual";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";

import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Search from "./Search";
import initialValues from "./schemas/initialValues";

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const bootstrapURLKeys = {
  key: GOOGLE_MAP_API_KEY,
  libraries: ["drawing", "places"],
};

const EMPTY_ARRAY = [];

const latLngToPoint = ({ lat, lng }) => ({ lat: lat(), lng: lng() });

const formatOverlayPath = (overlayPath) => {
  const path = [];
  overlayPath.forEach((point) => path.push(latLngToPoint(point)));
  return path;
};

function Map({ name, value, onChange, isEdit, mapCenter, setMapCenter, mapZoom, setMapZoom }) {
  const [map, setMap] = useState(null);
  const [googleMap, setGoogleMap] = useState(null);
  const [overlay, setOverlay] = useState(null);

  const isFinished = useMemo(() => value.length > 0, [value]);

  const drawingManager = useMemo(() => {
    if (googleMap) {
      return new googleMap.drawing.DrawingManager({
        drawingMode: isEdit ? null : googleMap.drawing.OverlayType.POLYGON,
        drawingControl: !isEdit,
        drawingControlOptions: {
          position: googleMap.ControlPosition.TOP_CENTER,
          drawingModes: ["polygon"],
        },
        polygonOptions: {
          editable: true,
        },
        map,
      });
    }
    return null;
  }, [googleMap, map, isEdit]);

  const onGoogleApiLoaded = useCallback(
    ({ map: _map, maps }) => {
      setMap(_map);
      setGoogleMap(maps);
      setOverlay(new maps.Polygon({ paths: [], map: _map }));
    },
    [setMap, setGoogleMap, setOverlay]
  );

  const reset = useCallback(() => {
    onChange(EMPTY_ARRAY);

    overlay.set("editable", true);
    drawingManager.setDrawingMode("polygon");
    drawingManager.setOptions({ drawingControl: true });
  }, [overlay, onChange, drawingManager]);

  const onSearchSelect = useCallback(
    async (search) => {
      if (map) {
        const [result] = await geocodeByAddress(search);
        const marker = await getLatLng(result);
        map.setCenter(marker);
        map.setZoom(17);
      }
    },
    [map]
  );

  useEffect(() => {
    if (googleMap && drawingManager && overlay) {
      googleMap.event.addListener(drawingManager, "overlaycomplete", (event) => {
        onChange(formatOverlayPath(event.overlay.getPath()));
        const overlayMap = event.overlay.getMap();
        setMapCenter(latLngToPoint(overlayMap.getCenter()));
        setMapZoom(overlayMap.getZoom());
        event.overlay.setMap(null);

        overlay.set("editable", false);
        drawingManager.setDrawingMode(null);
        drawingManager.setOptions({ drawingControl: false });
      });
    }
  }, [googleMap, drawingManager, overlay, onChange, setMapCenter, setMapZoom]);

  useEffect(() => {
    if (overlay) {
      const currentOverlayPath = overlay.getPath() ? formatOverlayPath(overlay.getPath()) : [];
      if (!isEqual(value, currentOverlayPath)) {
        overlay.setPath(value);
      }
    }
  }, [overlay, drawingManager, value]);

  return (
    <>
      <Grid container justifyContent="space-between" style={{ marginBottom: 20 }}>
        <Grid item xs={12}>{!!map && <Search onChange={onSearchSelect} />}</Grid>
        <Grid item style={{marginTop: 20}}>
          <MDButton disabled={!isFinished} onClick={reset} type="button" variant="gradient" color="secondary">
              Reset
            </MDButton>
        </Grid>
      </Grid>
      <Grid style={{ height: "300px", width: "calc(100% - 24px)" }} item xs={12}>
        <GoogleMapReact
          bootstrapURLKeys={bootstrapURLKeys}
          defaultCenter={initialValues.mapCenter}
          defaultZoom={initialValues.mapZoom}
          center={mapCenter}
          zoom={mapZoom}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={onGoogleApiLoaded}
        ></GoogleMapReact>
      </Grid>
      <MDBox mt={0.75}>
        <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
          <ErrorMessage name={name} />
        </MDTypography>
      </MDBox>
    </>
  );
}

Map.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  mapCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  setMapCenter: PropTypes.func.isRequired,
  mapZoom: PropTypes.number.isRequired,
  setMapZoom: PropTypes.func.isRequired,
};

export default Map;
